import {
	useCreateProductTagMutation,
	useGetProductTagsQuery,
	usePublishProductsToCommunityMutation,
	useUnpublishProductsFromCommunityMutation,
	useUpdateProductMutation,
} from '@codezo/graphql';
import { Check, Globe, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { DetailRow } from '../../components/ui/detail-row';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { SectionCard } from '../../components/ui/section-card';
import { SelectSearchInput } from '../../components/ui/select-primitives';
import { formatDate } from '../../lib/utils';

type Tag = { id: string; name: string };

/**
 * Community page controls for one product: publish state and the tags clients filter the page by.
 * What can be published is the backend's answer (communityPublishability), not re-derived here.
 */
export function CommunitySection({
	product,
	onChanged,
}: {
	product: {
		id: string;
		name: string;
		isPublishedToCommunity: boolean;
		communityPublishedAt?: string | null;
		communityPublishability?: { canPublish: boolean; reason?: string | null } | null;
		communityCopyCount?: number | null;
		tags?: Tag[] | null;
	};
	onChanged: () => void;
}) {
	const [publish, { loading: publishing }] = usePublishProductsToCommunityMutation();
	const [unpublish, { loading: unpublishing }] = useUnpublishProductsFromCommunityMutation();
	const [confirm, setConfirm] = useState<'publish' | 'unpublish' | null>(null);

	// Published but hidden: the timestamp is kept while the product is not ACTIVE, so it comes
	// back on its own when reactivated.
	const publishedButHidden = !product.isPublishedToCommunity && !!product.communityPublishedAt;
	const canPublish = product.communityPublishability?.canPublish ?? false;

	const handlePublish = async () => {
		try {
			const { data } = await publish({ variables: { productIds: [product.id] } });
			const result = data?.publishProductsToCommunity;
			if (!result?.success) {
				toast.error(result?.errors?.[0]?.message ?? result?.message ?? 'Failed to publish');
				return;
			}
			toast.success('Published to the community page');
			setConfirm(null);
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to publish');
		}
	};

	const handleUnpublish = async () => {
		try {
			const { data } = await unpublish({ variables: { productIds: [product.id] } });
			if (!data?.unpublishProductsFromCommunity?.success) {
				toast.error(data?.unpublishProductsFromCommunity?.message ?? 'Failed to unpublish');
				return;
			}
			toast.success('Removed from the community page');
			setConfirm(null);
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to unpublish');
		}
	};

	const status = product.isPublishedToCommunity
		? `Published ${product.communityPublishedAt ? formatDate(product.communityPublishedAt) : ''}`
		: publishedButHidden
			? 'Published, hidden while not active'
			: 'Not published';

	return (
		<>
			<SectionCard
				title="Community"
				action={
					product.communityPublishedAt ? (
						<button onClick={() => setConfirm('unpublish')} disabled={unpublishing}>
							<X size={12} />
							Unpublish
						</button>
					) : canPublish ? (
						<button onClick={() => setConfirm('publish')} disabled={publishing}>
							<Globe size={12} />
							Publish
						</button>
					) : undefined
				}
			>
				<div className="flex flex-col">
					<DetailRow label="Status" value={status} />
					{!product.communityPublishedAt && !canPublish && (
						<DetailRow
							label="Can't publish"
							value={product.communityPublishability?.reason ?? '—'}
						/>
					)}
					<DetailRow
						label="Taken by clients"
						value={String(product.communityCopyCount ?? 0)}
					/>
					<div className="flex flex-col gap-2 px-4 py-3">
						<span className="text-sm text-text-3">Tags</span>
						<TagEditor
							productId={product.id}
							tags={product.tags ?? []}
							onChanged={onChanged}
						/>
					</div>
				</div>
			</SectionCard>

			<ConfirmDialog
				open={confirm === 'publish'}
				onOpenChange={(open) => !open && setConfirm(null)}
				title="Publish to community"
				description={`"${product.name}" will be shown in every client's Community tab, where they can add a copy to their own products.`}
				confirmLabel="Publish"
				variant="default"
				loading={publishing}
				onConfirm={handlePublish}
			/>
			<ConfirmDialog
				open={confirm === 'unpublish'}
				onOpenChange={(open) => !open && setConfirm(null)}
				title="Unpublish from community"
				description={`"${product.name}" will be removed from clients' Community tab. Copies clients have already added are not affected.`}
				confirmLabel="Unpublish"
				variant="default"
				loading={unpublishing}
				onConfirm={handleUnpublish}
			/>
		</>
	);
}

/* ── Tag editor ── */

function TagEditor({
	productId,
	tags,
	onChanged,
}: {
	productId: string;
	tags: Tag[];
	onChanged: () => void;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const { data, refetch } = useGetProductTagsQuery({
		variables: { input: { pagination: { limit: 500, currentPage: 1 } } },
		fetchPolicy: 'no-cache',
	});
	const [updateProduct, { loading: saving }] = useUpdateProductMutation();
	const [createTag, { loading: creating }] = useCreateProductTagMutation();

	const allTags = useMemo(
		() =>
			[...(data?.getAllProductTag?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
		[data]
	);
	const selectedIds = new Set(tags.map((t) => t.id));
	const trimmed = query.trim();
	const filtered = trimmed
		? allTags.filter((t) => t.name.toLowerCase().includes(trimmed.toLowerCase()))
		: allTags;
	const exactMatch = allTags.some((t) => t.name.toLowerCase() === trimmed.toLowerCase());

	// updateProduct replaces the whole tag set, so always send the full list.
	const saveTags = async (tagIds: string[]) => {
		try {
			const { data } = await updateProduct({
				variables: { input: { id: productId, tagIds } },
			});
			if (!data?.updateProduct?.success) {
				toast.error(data?.updateProduct?.message ?? 'Failed to update tags');
				return;
			}
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update tags');
		}
	};

	const toggle = (tagId: string) =>
		saveTags(
			selectedIds.has(tagId)
				? tags.filter((t) => t.id !== tagId).map((t) => t.id)
				: [...tags.map((t) => t.id), tagId]
		);

	const handleCreate = async () => {
		try {
			const { data: created } = await createTag({ variables: { input: { name: trimmed } } });
			const newId = created?.createProductTag?.referenceKey;
			if (!newId) {
				toast.error(created?.createProductTag?.message ?? 'Failed to create tag');
				return;
			}
			setQuery('');
			await refetch();
			await saveTags([...tags.map((t) => t.id), newId]);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create tag');
		}
	};

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{tags.map((tag) => (
				<span
					key={tag.id}
					className="inline-flex items-center gap-1 rounded-[var(--r-1)] border border-line-2 bg-surface py-0.5 pr-1 pl-2.5 text-sm text-text-2"
				>
					{tag.name}
					<button
						onClick={() => toggle(tag.id)}
						disabled={saving}
						aria-label={`Remove ${tag.name}`}
						className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[var(--r-1)] bg-transparent text-text-3 hover:text-text-1"
					>
						<X size={11} />
					</button>
				</span>
			))}
			<Popover
				open={open}
				onOpenChange={(next) => {
					setOpen(next);
					if (!next) setQuery('');
				}}
			>
				<PopoverTrigger className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-[var(--r-1)] border border-dashed border-line-2 bg-transparent px-2 text-sm text-text-3 transition-colors hover:border-line-3 hover:text-text-1">
					<Plus size={11} />
					Add tag
				</PopoverTrigger>
				<PopoverContent align="start" className="w-[240px] gap-0 p-0">
					<SelectSearchInput
						value={query}
						onChange={setQuery}
						placeholder="Find or create a tag…"
					/>
					<div className="scroll-thin max-h-[220px] overflow-y-auto p-1">
						{filtered.map((tag) => (
							<button
								key={tag.id}
								onClick={() => toggle(tag.id)}
								disabled={saving}
								className="flex w-full cursor-pointer items-center justify-between rounded-[var(--r-1)] px-2 py-1.5 text-left text-sm text-text-1 hover:bg-surface-hover"
							>
								{tag.name}
								{selectedIds.has(tag.id) && (
									<Check size={12} className="text-accent" />
								)}
							</button>
						))}
						{trimmed && !exactMatch && (
							<button
								onClick={handleCreate}
								disabled={creating || saving}
								className="flex w-full cursor-pointer items-center gap-1.5 rounded-[var(--r-1)] px-2 py-1.5 text-left text-sm text-text-2 hover:bg-surface-hover"
							>
								<Plus size={12} />
								Create “{trimmed}”
							</button>
						)}
						{!trimmed && filtered.length === 0 && (
							<div className="px-2 py-3 text-center text-sm text-text-4">
								No tags yet. Type to create one.
							</div>
						)}
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
}
